import React, { useEffect, useState } from 'react';
import { MonitorSmartphone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { listMySessions, revokeOtherSessions } from '../services/api';

const describeAgent = (ua = '') => {
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac OS/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : 'Unknown device';
  const browser = /Edg\//.test(ua) ? 'Edge' : /CriOS|Chrome\//.test(ua) ? 'Chrome' : /FxiOS|Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  return `${browser} on ${os}`;
};

const fmt = (iso) => new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

export const SessionsDialog = ({ open, onOpenChange }) => {
  const [sessions, setSessions] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => listMySessions().then((d) => setSessions(d.sessions)).catch(() => setSessions([]));
  useEffect(() => { if (open) { setSessions(null); load(); } }, [open]);

  const signOutOthers = async () => {
    setBusy(true);
    try {
      const { revoked } = await revokeOtherSessions();
      toast.success(revoked ? `Signed out ${revoked} other device${revoked === 1 ? '' : 's'}` : 'No other devices were signed in');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not sign out other devices');
    } finally {
      setBusy(false);
    }
  };

  const others = (sessions || []).filter((s) => !s.current).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="sessions-dialog">
        <DialogHeader>
          <DialogTitle>Signed-in devices</DialogTitle>
          <DialogDescription>Sessions stay signed in for up to 30 days of inactivity.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="sessions-list">
          {!sessions && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
          {sessions?.map((s) => (
            <div key={s.id} className="flex items-start gap-3 border border-slate-200 rounded-md p-3" data-testid={`session-row-${s.id}`}>
              <MonitorSmartphone className="h-4 w-4 mt-0.5 text-slate-500 shrink-0" />
              <div className="min-w-0 flex-1 text-sm">
                <div className="font-medium text-slate-900 flex items-center gap-2">
                  {describeAgent(s.userAgent)}
                  {s.current && <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded" data-testid="session-current-badge">This device</span>}
                </div>
                <div className="text-xs text-slate-500">Last active {fmt(s.lastSeenAt)}{s.ip ? ` · ${s.ip}` : ''}</div>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sessions-close-btn">Close</Button>
          <Button onClick={signOutOthers} disabled={busy || !others} className="bg-slate-900 hover:bg-slate-800" data-testid="sign-out-other-devices-btn">
            {busy ? 'Signing out…' : 'Sign out other devices'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionsDialog;
